/**
 * Site Health → Google Business Profile recommendations (Local Falcon).
 *
 * Backend contract: GET /site-health/gbp-recommendations?tenant_id=...
 * Returns recommendations (what to fix, with the reason) and completed items.
 *
 * Note there is deliberately NO place_id parameter. One agency Local Falcon
 * account holds every client's location, so the backend resolves the location
 * from the tenant mapping; accepting it from the caller would let anyone read
 * another client's report by editing the URL.
 *
 * Heat maps / grid scans are deliberately absent — the team reported their
 * exports are unreliable, so only the analysis is surfaced.
 */
import { apiClient } from "./apiClient";

export interface GbpRecommendation {
	title: string;
	severity: "major" | "minor" | string;
	reason: string;
	source: string;
}

/** A directory this business should be listed on. */
export interface GbpCitation {
	name: string;
	/** Empty when the source gave no usable http(s) link. */
	url: string;
}

/** A competitor the analysis judges beatable, and why. */
export interface GbpCompetitor {
	name: string;
	reason: string;
}

/** The Local Falcon location mapped to this client. */
export interface GbpLocation {
	placeId: string;
	name: string;
	lat: string;
	lng: string;
	/** A scan needs coordinates; without them only reading is possible. */
	canScan: boolean;
}

export interface GbpRecommendationsResult {
	tenantId: string;
	/** False when no Local Falcon key is stored — NOT the same as "all clear". */
	connected: boolean;
	/** False when connected but this client has no location assigned yet. */
	mapped: boolean;
	location: GbpLocation | null;
	/** Every listing this client owns. More than one = show a branch picker. */
	locations: GbpLocation[];
	reportKey: string | null;
	scannedAt: string | null;
	keyword: string | null;
	summary: string;
	recommendations: GbpRecommendation[];
	completed: GbpRecommendation[];
	citations: GbpCitation[];
	vulnerable: GbpCompetitor[];
	/** Analysis arrived but could not be read — must not render as "nothing to do". */
	unreadable: boolean;
	/**
	 * The latest report has no analysis at all, because the scan was run with
	 * the AI analysis turned off. A third state: not "unreadable", and above
	 * all not "nothing to fix".
	 */
	analysisMissing: boolean;
	error: string | null;
}

export interface QuickScanParams {
	tenantId: string;
	keyword: string;
	gridSize: number;
	radius: number;
	measurement: "mi" | "km";
	/**
	 * Where the grid sits, when the operator plotted it on the map. Omitted
	 * entirely when they left it on the business — the backend then uses the
	 * mapped listing's own coordinates, which stays the safe default.
	 */
	centerLat?: number;
	centerLng?: number;
	/**
	 * Buy the written Falcon AI analysis (a flat 25 credits). Default on: it is
	 * what the recommendations page renders, and the vendor's API offers no way
	 * to add one to a report later — only a fresh, fully-charged re-scan.
	 */
	aiAnalysis?: boolean;
}

export interface QuickScanRun {
	id: string;
	/**
	 * "reused" is TERMINAL and costs nothing — it found a recent identical
	 * scan. Every other terminal status followed a real, billed submission:
	 * the backend's dry-run switch was removed on 2026-09-02, so there is no
	 * longer any outcome that means "we pretended".
	 */
	status:
		| "pending"
		| "processing"
		| "complete"
		| "failed"
		| "reused"
		| string;
	keyword: string;
	reportKey: string | null;
	error: string | null;
	/** Human-readable note for the no-spend outcomes. */
	detail: string;
	/** True once a request has left our side and may have been billed. */
	spent: boolean;
	estimatedCredits: number;
}

type RawObject = Record<string, unknown>;

const asString = (value: unknown): string =>
	typeof value === "string" ? value : "";

const normalizeItem = (raw: unknown): GbpRecommendation => {
	const obj = (raw ?? {}) as RawObject;
	return {
		title: asString(obj.title),
		severity: asString(obj.severity) || "minor",
		reason: asString(obj.reason),
		source: asString(obj.source) || "profile_scan",
	};
};

const normalizeList = (raw: unknown): GbpRecommendation[] =>
	Array.isArray(raw) ? raw.map(normalizeItem).filter((i) => i.title) : [];

const normalizeCitations = (raw: unknown): GbpCitation[] =>
	Array.isArray(raw)
		? raw
				.map((entry) => {
					const obj = (entry ?? {}) as RawObject;
					return { name: asString(obj.name), url: asString(obj.url) };
				})
				.filter((c) => c.name || c.url)
		: [];

const normalizeCompetitors = (raw: unknown): GbpCompetitor[] =>
	Array.isArray(raw)
		? raw
				.map((entry) => {
					const obj = (entry ?? {}) as RawObject;
					return { name: asString(obj.name), reason: asString(obj.reason) };
				})
				.filter((c) => c.name)
		: [];

const normalizeLocation = (raw: unknown): GbpLocation | null => {
	if (!raw || typeof raw !== "object") return null;
	const obj = raw as RawObject;
	const placeId = asString(obj.place_id);
	if (!placeId) return null;
	return {
		placeId,
		name: asString(obj.name),
		lat: asString(obj.lat),
		lng: asString(obj.lng),
		canScan: Boolean(obj.can_scan),
	};
};

const normalizeRun = (raw: unknown): QuickScanRun => {
	const obj = (raw ?? {}) as RawObject;
	return {
		id: asString(obj.id),
		status: asString(obj.status) || "pending",
		keyword: asString(obj.keyword),
		reportKey: asString(obj.report_key) || null,
		error: asString(obj.error) || null,
		detail: asString(obj.detail),
		spent: Boolean(obj.spent),
		estimatedCredits:
			typeof obj.estimated_credits === "number" ? obj.estimated_credits : 0,
	};
};

export const gbpRecommendationsService = {
	/**
	 * @param placeId optional branch to show. The SERVER decides whether to
	 * honour it — it is accepted only when it is one of this tenant's own
	 * listings, so a tampered value cannot reach another client's report.
	 * @param reportKey optional past scan to show instead of the latest,
	 * validated server-side the same way.
	 */
	async get(
		tenantId: string,
		placeId = "",
		reportKey = "",
	): Promise<GbpRecommendationsResult> {
		const params = new URLSearchParams({ tenant_id: tenantId });
		if (placeId) params.set("place_id", placeId);
		// Empty = the latest scan, which is the ordinary case. A key sends the
		// server looking for that specific past scan, and it is honoured only
		// if it belongs to this tenant's own listing — the same rule as
		// place_id, for the same reason.
		if (reportKey) params.set("report_key", reportKey);
		const { data } = await apiClient.get(
			`/site-health/gbp-recommendations?${params.toString()}`,
		);
		const raw = (data ?? {}) as RawObject;
		return {
			tenantId: asString(raw.tenant_id) || tenantId,
			connected: Boolean(raw.connected),
			mapped: Boolean(raw.mapped),
			location: normalizeLocation(raw.location),
			locations: Array.isArray(raw.locations)
				? raw.locations
						.map(normalizeLocation)
						.filter((l): l is GbpLocation => l !== null)
				: [],
			reportKey: asString(raw.report_key) || null,
			scannedAt: asString(raw.scanned_at) || null,
			keyword: asString(raw.keyword) || null,
			summary: asString(raw.summary),
			recommendations: normalizeList(raw.recommendations),
			completed: normalizeList(raw.completed),
			citations: normalizeCitations(raw.citations),
			vulnerable: normalizeCompetitors(raw.vulnerable),
			unreadable: Boolean(raw.unreadable),
			analysisMissing: Boolean(raw.analysis_missing),
			error: asString(raw.error) || null,
		};
	},

	/**
	 * Start a scan. THIS SPENDS LOCAL FALCON CREDITS — roughly one per grid
	 * point plus 25 for the AI analysis. Only ever call it from an explicit,
	 * confirmed operator action, never from a render or a refetch.
	 */
	async startQuickScan(params: QuickScanParams): Promise<QuickScanRun> {
		const { data } = await apiClient.post(
			"/site-health/gbp-quick-scan",
			{
				tenant_id: params.tenantId,
				keyword: params.keyword,
				grid_size: params.gridSize,
				radius: params.radius,
				measurement: params.measurement,
				// Sent only when plotted: a null centre would be a request to
				// scan nowhere, an absent one means "the business's own spot".
				...(params.centerLat !== undefined && params.centerLng !== undefined
					? { center_lat: params.centerLat, center_lng: params.centerLng }
					: {}),
				ai_analysis: params.aiAnalysis !== false,
			},
			// The client default (30s) is shorter than this request honestly
			// takes: on 2026-09-03 a scan needed 60s end to end and the browser
			// abandoned a POST that had already spent 34 credits. The vendor
			// documents eager=true as "responds within 20 seconds" but took
			// 39.5s, so that bound cannot be leaned on. findInFlightScan covers
			// the cases this still does not.
			{ timeout: 120_000 },
		);
		return normalizeRun(data);
	},

	/**
	 * The scan already running for this client, if one is.
	 *
	 * Free, and it can never START a scan — it only reads reports. Used when a
	 * start request dies in transit, so the operator is not left paying for a
	 * scan they cannot see.
	 */
	async findInFlightScan(
		tenantId: string,
		placeId = "",
	): Promise<QuickScanRun | null> {
		const params = new URLSearchParams({ tenant_id: tenantId });
		if (placeId) params.set("place_id", placeId);
		const { data } = await apiClient.get(
			`/site-health/gbp-scan-in-flight?${params.toString()}`,
		);
		const raw = (data ?? {}) as RawObject;
		return raw.run ? normalizeRun(raw.run) : null;
	},

	/**
	 * `aiAnalysis` must echo what the scan was STARTED with. The backend waits
	 * for the written analysis before calling a scan complete, so polling a
	 * no-analysis scan as though it had one never terminates.
	 */
	/**
	 * Search terms worth scanning for this client.
	 *
	 * Free: suggesting spends no scan credits, only a cached LLM call. An
	 * operator who ignores these and types their own is the unchanged path, so
	 * a failure here resolves to an empty list rather than surfacing an error.
	 */
	async suggestKeywords(
		tenantId: string,
		placeId = "",
	): Promise<string[]> {
		const params = new URLSearchParams({ tenant_id: tenantId });
		if (placeId) params.set("place_id", placeId);
		const { data } = await apiClient.get(
			`/site-health/gbp-keyword-suggestions?${params.toString()}`,
		);
		const raw = (data ?? {}) as RawObject;
		return Array.isArray(raw.suggestions)
			? raw.suggestions.filter((s): s is string => typeof s === "string")
			: [];
	},

	async getScanStatus(
		tenantId: string,
		runId: string,
		aiAnalysis: boolean = true,
	): Promise<QuickScanRun> {
		const params = new URLSearchParams({
			tenant_id: tenantId,
			ai_analysis: String(aiAnalysis),
		});
		const { data } = await apiClient.get(
			`/site-health/gbp-quick-scan/${runId}/status?${params.toString()}`,
		);
		return normalizeRun(data);
	},
};

/**
 * Credits a scan will consume: one per grid point, plus the AI analysis fee.
 * Shown to the operator BEFORE they confirm, so the cost is never a surprise.
 */
export const AI_ANALYSIS_CREDITS = 25;

export const estimateScanCredits = (
	gridSize: number,
	aiAnalysis: boolean = true,
): number => gridSize * gridSize + (aiAnalysis ? AI_ANALYSIS_CREDITS : 0);
